// Turns socket payloads into log entries, and log entries into lines. Pure —
// data in, data out — so the reset-vs-toggle rule and the wording are both
// testable without a socket or a rendered route.

/**
 * Entries for a READY_TO_SKIP_UPDATED broadcast.
 *
 * The event is shared: pressing ready and withdrawing it both fire it, but so
 * do three resets (bidding opening, a bid landing, the single-player path).
 * Only a toggle carries `playerId`, and only a toggle is worth logging —
 * without that check every bid would announce the whole room un-readying.
 *
 * @param {object} payload READY_TO_SKIP_UPDATED payload.
 * @param {Array<{id: string, username: string}>} players Known players.
 *
 * @return {Array<{key: string, params?: object}>} Zero to two entries, in the
 *   order they should be logged.
 */
export function readyToSkipLines(payload, players = []) {
  const { playerId, ready, everyoneReady } = payload ?? {};
  if (!playerId) return [];

  // A key, not a sentence: the caller resolves it, because a player who has
  // already left should still be named in the language the reader is using.
  const name = players?.find(p => p.id === playerId)?.username ?? 'log.aPlayer';

  const entries = [{
    key: ready ? 'log.readyToSkip' : 'log.noLongerReady',
    params: { name },
  }];
  if (everyoneReady) entries.push({ key: 'log.everyoneReady' });

  return entries;
}

// Row colours, keyed rather than matched against the rendered sentence. The
// English regexes this replaces (/\bbid\b/, /\bwon\b/ …) matched nothing at
// all in any other language, so every row in a translated log went grey.
const KINDS = [
  [/^log\.mission/, '#d94f2b', '#f0dcc0'],
  [/^log\.(bid|rebid)/, '#e0a325', '#cbb493'],
  [/^log\.(won|gameOver)/, '#2f9e6e', '#cbb493'],
  [/^log\.(failed|skipped)/, '#a8552f', '#cbb493'],
];

const NEUTRAL = { tick: '#5c3a1e', color: '#8a6e4c' };

/**
 * The tick and text colour for a log entry's key.
 *
 * @param {?string} key An entry's key, or undefined for a legacy entry.
 *
 * @return {{tick: string, color: string}}
 */
export function kindOfKey(key) {
  if (!key) return NEUTRAL;
  const hit = KINDS.find(([re]) => re.test(key));
  return hit ? { tick: hit[1], color: hit[2] } : NEUTRAL;
}

/**
 * One log entry as a line of text.
 *
 * @param {{key?: string, params?: object, text?: string}} entry
 * @param {function(string, object=): string} t The active locale's translator.
 *
 * @return {string}
 */
export function renderEntry(entry, t) {
  // A legacy entry is already a finished sentence and has no key to look up.
  if (!entry?.key) return entry?.text ?? '';
  return t(entry.key, entry.params);
}
